import React, { useState, useEffect, useRef, Suspense } from "react";
import { ImageProvider } from "../../../utils/image-provider";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { Link } from "gatsby";
import ReactMarkdown from "react-markdown";
import "../home.css";

const Slider = React.lazy(() => import("react-slick"));

/* ✅ Memoized Markdown Component */
const MarkdownContent = React.memo(({ content, className }) => {
  if (!content) return null;

  return (
    <div className={className}>
      <ReactMarkdown>{content}</ReactMarkdown>
    </div>
  );
});

const SlidingBanner = ({ data }) => {
  const { left_arrow, right_arrow } = data || {};
  const slider_data = data?.banner_images;

  const sliderRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);
  const [enableAutoplay, setEnableAutoplay] = useState(false);

  // Detect device (render only one layout)
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 1024);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Delay autoplay for LCP improvement
  useEffect(() => {
    const timer = setTimeout(() => {
      setEnableAutoplay(true);
      sliderRef.current?.slickPlay();
    }, 3000);
    return () => clearTimeout(timer);
  }, []);

  const PrevArrow = ({ onClick }) => (
    <div
      onClick={onClick}
      className="absolute left-[5%] lg:left-[1%] lg:top-[50%] top-[95%] -translate-y-1/2 z-20 cursor-pointer"
    >
      <img
        src={left_arrow?.url}
        alt="Previous"
        className="h-[8vw] w-[8vw] xl:h-[5vh] xl:w-[5vh]"
        loading="lazy"
      />
    </div>
  );

  const NextArrow = ({ onClick }) => (
    <div
      onClick={onClick}
      className="absolute right-[5%] lg:right-[1%] lg:top-[50%] top-[95%] -translate-y-1/2 z-20 cursor-pointer"
    >
      <img
        src={right_arrow?.url}
        alt="Next"
        className="h-[8vw] w-[8vw] xl:h-[5vh] xl:w-[5vh]"
        loading="lazy"
      />
    </div>
  );

  const settings = {
    dots: true,
    dotsClass: "slick-dots custom-dots",
    infinite: true,
    autoplay: enableAutoplay,
    speed: 600,
    cssEase: "ease-out",
    autoplaySpeed: 6000,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: true,
    prevArrow: <PrevArrow />,
    nextArrow: <NextArrow />,
    pauseOnHover: false,
  };

  return (
    <div id="home_slick" className="relative overflow-hidden">
      <Suspense fallback={<div className="h-[170vw] lg:h-[40vw]" />}>
        <Slider ref={sliderRef} {...settings}>
          {slider_data
            ?.filter((_, index) => index !== 1)
            ?.map((item, index) => {
              const description =
                item?.description_1?.data?.description_1 || "";

              return (
                <div key={index} className="relative">
                  <Link
                    to={item?.link_redirection}
                    target={item?.same_or_different_tab || undefined}
                  >
                    {isMobile ? (
                      <div className="w-full h-[170vw] relative">
                        <img
                          src={item?.mobile_image?.url}
                          alt={
                            item?.mobile_image?.alternativeText ||
                            "Mobile Banner"
                          }
                          className="object-cover w-full h-full"
                          loading={index === 0 ? "eager" : "lazy"}
                          fetchPriority={index === 0 ? "high" : "auto"}
                        />
                        <div className="absolute top-1/2 -translate-y-1/2 w-full text-white">
                          <div className="w-[85%] mx-auto drop-shadow-lg">
                            {item?.banner_text_1 && (
                              <p className="font-redhatMedium text-[4.5vw]">
                                {item?.banner_text_1}
                              </p>
                            )}
                            {item?.heading && (
                              <p className="font-redhatBold text-[5vw]">
                                {item?.heading}
                              </p>
                            )}
                            <MarkdownContent
                              content={description}
                              className="font-redhatRegular text-[3.5vw] prose prose-invert max-w-none"
                            />
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="w-full h-full relative">
                        <img
                          src={item?.image?.url}
                          alt={item?.image?.alternativeText || "Banner image"}
                          className="w-full h-full object-cover"
                          loading={index === 0 ? "eager" : "lazy"}
                        />
                        <div className="absolute top-[8vw] right-[2vw] w-[45%] text-white drop-shadow-lg">
                          {item?.banner_text_1 && (
                            <p className="font-redhatMedium text-[2.5vw]">
                              {item?.banner_text_1}
                            </p>
                          )}

                          {item?.heading && (
                            <p className="font-redhatBold text-[3.5vw] py-[1vw]">
                              {item?.heading}
                            </p>
                          )}

                          <MarkdownContent
                            content={description}
                            className="font-redhatRegular text-[1.3vw] prose prose-invert max-w-none"
                          />
                        </div>
                      </div>
                    )}
                  </Link>
                </div>
              );
            })}
        </Slider>
      </Suspense>
    </div>
  );
};

export default SlidingBanner;
